import { useMutation, useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

type EntityType = 'customer_engagement' | 'audit_task' | 'task_resolution' | 'audit_engagement'
type CommentType = 'simple_comment'

type PostCommentRequest = {
  targetEntityId?: string
  targetEntityType: EntityType
  content: string
  mentionedUserIds?: string[]
}
type PostCommentResponse = { id: string }

type GetCommentRequestParams = {
  targetEntityId?: string
  targetEntityType: EntityType
}

type MentionableUserResponse = {
  id: string
  name: string
  email: string
  role: string
  avatarUrl: string
}

type GetMineUnreadComments = {
  engagements: number
  tasks: number
}

type GetReadUnreadCommentsByEngagement = {
  engagementId: string
  totalComments: number
  totalUnreadComments: number
  totalTasksComments: number
  totalTasksUnreadComments: number
  tasks: {
    taskId: string
    totalComments: number
    totalUnreadComments: number
  }[]
}

type CommentResponse = {
  comments: {
    id: string
    targetEntityId: string
    targetEntityType: EntityType
    type: CommentType
    createdAt: Date
    content: string
    isRead: boolean
    owner: MentionableUserResponse
    replies: CommentResponse['comments']
    mentions: MentionableUserResponse[]
    likes: MentionableUserResponse[]
  }[]
  total: number
  totalUnread: number
}

export function useSendComment() {
  return useMutation({
    mutationFn: async (payload: PostCommentRequest) => {
      const { data } = await apiClient.post<PostCommentResponse>('/int/v3/platform/comments', payload)
      return { id: data.id }
    },
  })
}

export function useReplyComment() {
  return useMutation({
    mutationFn: async ({ commentId, ...payload }: PostCommentRequest & { commentId: string }) => {
      const { data } = await apiClient.post<PostCommentResponse>(
        `/int/v3/platform/comments/${commentId}/reply`,
        payload
      )
      return { id: data.id }
    },
  })
}

export function useDeleteComment() {
  return useMutation({
    mutationFn: async (commentId: string) => {
      const { data } = await apiClient.delete<PostCommentResponse>(`/int/v3/platform/comments/${commentId}`)
      return { id: data.id }
    },
  })
}

export function useMarkAsRead() {
  return useMutation({
    mutationFn: async (params: GetCommentRequestParams) => {
      await apiClient.put(
        `/int/v3/platform/comments/read?targetEntityId=${params.targetEntityId}&targetEntityType=${params.targetEntityType}`
      )
    },
  })
}

export function useMarkAsUnread() {
  return useMutation({
    mutationFn: async (params: GetCommentRequestParams) => {
      await apiClient.put(
        `/int/v3/platform/comments/recent/unread?targetEntityId=${params.targetEntityId}&targetEntityType=${params.targetEntityType}`
      )
    },
  })
}

export function useGetComments(params: GetCommentRequestParams, disabled?: boolean) {
  return useQuery<CommentResponse>({
    queryKey: ['comments', params.targetEntityId, params.targetEntityType],
    queryFn: async () => {
      const { data } = await apiClient.get('/int/v3/platform/comments', { params })
      return data
    },
    enabled: Boolean(params.targetEntityId) && !disabled,
  })
}

export function useGetMentionableUsers(params: GetCommentRequestParams, disabled?: boolean) {
  return useQuery<MentionableUserResponse[]>({
    queryKey: ['mentionable-users', params],
    queryFn: async () => {
      const { data } = await apiClient.get('/int/v3/platform/comments/mentionable-users', { params })
      return data
    },
    enabled: !disabled,
  })
}

export function useLike() {
  return useMutation({
    mutationFn: async (commentId: string) => {
      const { data } = await apiClient.put<PostCommentResponse>(`/int/v3/platform/comments/${commentId}/like`)
      return { id: data.id }
    },
  })
}

export function useDeleteLike() {
  return useMutation({
    mutationFn: async (commentId: string) => {
      const { data } = await apiClient.delete<PostCommentResponse>(
        `/int/v3/platform/comments/${commentId}/like`
      )
      return { id: data.id }
    },
  })
}

export function useGetMineUnread(programId?: string) {
  return useQuery<GetMineUnreadComments>({
    queryKey: ['get-mine-unread-comments', programId],
    queryFn: async () => {
      const { data } = await apiClient.get('/int/v3/platform/comments/mine/unread', { params: { programId } })
      return data
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(programId),
  })
}

export function useGetReadUnreadByEngagement(engagementId?: string, disabled?: boolean) {
  return useQuery<GetReadUnreadCommentsByEngagement>({
    queryKey: ['read-unread-comments-by-engagement', engagementId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/int/v3/platform/comments/read?engagementId=${engagementId}`)
      return data
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(engagementId) && !disabled,
  })
}

export function useGetMineUnreadAsOperator(operatorId?: string) {
  return useQuery<GetMineUnreadComments>({
    queryKey: ['get-mine-unread-comments-operator', operatorId],
    queryFn: async () => {
      const { data } = await apiClient.get('/int/v3/platform/comments/mine/unread', {
        params: { operatorId },
      })
      return data
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(operatorId),
  })
}

export function useBulkSendComment() {
  return useMutation({
    mutationFn: async (comments: PostCommentRequest[]) => {
      return await apiClient.post<PostCommentResponse>(`/int/v3/platform/comments/bulk`, { comments })
    },
  })
}
